import { useMemo } from 'react';
import { Smartphone, Bell, User as UserIcon, Menu } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  MOBILE_ACTION_ICON,
  MAX_MOBILE_HEADER_ACTIONS,
  setMobileHeaderActions,
  useMobileHeaderActions,
  type MobileHeaderActionId,
  type MobileHeaderActionMeta,
} from '@/lib/mobileHeaderActions';
import { useMobileShortcutAvailability } from '@/lib/useMobileShortcutAvailability';

export default function MobileHeaderActionsTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const selected = useMobileHeaderActions(user?.id);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Catalogo condiviso filtrato per le feature attive del tenant/utente:
  // se per esempio Wiki o AI Tools non sono abilitati, non compaiono qui
  // (così l'utente non può sceglierli) e neanche nel renderer dell'header.
  const { visibleCatalog, isAvailable } = useMobileShortcutAvailability();

  const toggle = (id: MobileHeaderActionId, on: boolean) => {
    if (on) {
      if (selectedSet.has(id)) return;
      if (selected.length >= MAX_MOBILE_HEADER_ACTIONS) {
        toast({
          title: 'Limite raggiunto',
          description: `Puoi avere al massimo ${MAX_MOBILE_HEADER_ACTIONS} azioni nell'header mobile. Disattivane una per aggiungerne un'altra.`,
          variant: 'destructive',
        });
        return;
      }
      setMobileHeaderActions(user?.id, [...selected, id]);
    } else {
      if (!selectedSet.has(id)) return;
      setMobileHeaderActions(user?.id, selected.filter((s) => s !== id));
    }
  };

  const renderRow = (meta: MobileHeaderActionMeta) => {
    const isOn = selectedSet.has(meta.id);
    const Icon = MOBILE_ACTION_ICON[meta.id];
    return (
      <div
        key={meta.id}
        className="flex items-center justify-between gap-4 py-3"
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
              isOn
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <Label className="text-sm font-medium block">{meta.label}</Label>
            <p className="text-xs text-muted-foreground">{meta.description}</p>
          </div>
        </div>
        <Switch
          checked={isOn}
          onCheckedChange={(checked) => toggle(meta.id, checked)}
          aria-label={`Mostra ${meta.label} nell'header mobile`}
        />
      </div>
    );
  };

  // Anteprima: mostriamo solo le voci ancora disponibili. Se l'utente aveva
  // selezionato in passato una voce ora disabilitata, non la disegniamo.
  const previewIds = selected.filter(isAvailable);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          Header mobile
        </CardTitle>
        <CardDescription>
          Scegli quali azioni rapide vedere nella barra in alto quando usi l'app
          su smartphone. Fino a {MAX_MOBILE_HEADER_ACTIONS} azioni.
          Le scelte sono salvate solo su questo dispositivo.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Anteprima
          </p>
          <div className="rounded-xl border bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 p-3 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <button
                disabled
                className="shrink-0 p-2 rounded-md text-gray-500 dark:text-gray-400"
                aria-label="Menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-1 flex-1 justify-end overflow-hidden">
                {previewIds.map((id) => {
                  const Icon = MOBILE_ACTION_ICON[id];
                  const meta = visibleCatalog.find((m) => m.id === id);
                  return (
                    <div
                      key={id}
                      className="shrink-0 p-2 rounded-md text-gray-600 dark:text-gray-300"
                      title={meta?.label}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                  );
                })}
                <div className="shrink-0 p-2 rounded-md text-gray-600 dark:text-gray-300">
                  <Bell className="h-5 w-5" />
                </div>
                <div className="shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold ring-2 ring-white/50 dark:ring-gray-700/50">
                  <UserIcon className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {selected.length} di {MAX_MOBILE_HEADER_ACTIONS} azioni selezionate.
            Le voci non selezionate restano accessibili dal menu laterale.
          </p>
        </div>

        <div className="divide-y divide-border">
          {visibleCatalog.map(renderRow)}
        </div>
      </CardContent>
    </Card>
  );
}
